import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useGuestRtc, RemotePeer } from '@/hooks/useGuestRtc';
const GuestSignModal = lazy(() => import('@/components/GuestSignModal').then((m) => ({ default: m.GuestSignModal }))); // pre223: lazy (pdf.js erst beim Signieren)
import { ErrorBoundary } from '@/components/ErrorBoundary';

// pre214: Bild-im-Bild starten — Standard-API + iOS-Safari-Fallback (webkitSetPresentationMode).
async function enterPip(v: HTMLVideoElement | null): Promise<boolean> {
  if (!v) return false;
  try {
    if ((document as any).pictureInPictureElement) return true;
    const stream = v.srcObject as MediaStream | null;
    if (!stream || stream.getVideoTracks().length === 0) return false;
    if (typeof v.requestPictureInPicture === 'function' && (document as any).pictureInPictureEnabled) {
      await v.requestPictureInPicture(); return true;
    }
    const wk = (v as any).webkitSetPresentationMode; // iOS Safari
    if (typeof wk === 'function') { wk.call(v, 'picture-in-picture'); return true; }
  } catch { /* Nutzergeste nötig oder nicht unterstützt */ }
  return false;
}

/** Welle 8d / Mesh M1 — Call-Oberfläche für den Gast/Experten. */
export function GuestCall({ rtcToken, caller = false, autoClose = false }: { rtcToken: string; caller?: boolean; autoClose?: boolean }) {
  const rtc = useGuestRtc(rtcToken, caller);
  const localRef = useRef<HTMLVideoElement>(null);
  const primaryVideoRef = useRef<HTMLVideoElement | null>(null); // pre214: Berater-Video für PiP
  const bgAudioRef = useRef<HTMLAudioElement | null>(null); // pre217: Hintergrund-Audio (iOS)
  const fileRef = useRef<HTMLInputElement>(null); // pre215: Screenshot/Bild-Auswahl
  const [imgHint, setImgHint] = useState<string | null>(null);

  useEffect(() => {
    if (localRef.current && rtc.localStream) localRef.current.srcObject = rtc.localStream;
  }, [rtc.localStream]);

  // pre214: Beim Verlassen des Browsers (Tab in den Hintergrund) das Berater-Video ins
  // Bild-im-Bild schieben → Audio+Video laufen weiter, statt zu pausieren. Der
  // autoPictureInPicture-Hint lässt moderne Browser (iOS Safari, Chrome) das selbst tun;
  // der visibilitychange-Aufruf ist ein Best-Effort-Fallback (kann ohne Nutzergeste scheitern).
  useEffect(() => {
    const v = primaryVideoRef.current;
    if (!v) return;
    try { (v as any).autoPictureInPicture = true; v.setAttribute('autopictureinpicture', ''); } catch { /* */ }
    // pre217: Das separate Audio-Element mit der Tonspur des Beraters "warmlaufen" lassen
    // (stumm, autoplay erlaubt). Beim Verlassen des Tabs schalten wir den Ton dorthin um —
    // ein bereits spielendes, dann entstummtes Element überlebt auf iOS den Hintergrund
    // besser als die pausierende Videospur. Im Vordergrund bleibt der Ton am Video (kein Doppel-Ton).
    const stream = rtc.peers[0]?.stream;
    const a = bgAudioRef.current;
    if (a && stream) {
      const at = stream.getAudioTracks();
      if (at.length > 0) {
        try { a.srcObject = new MediaStream(at); a.muted = true; void a.play().catch(() => { /* */ }); } catch { /* */ }
      }
    }
    const onVis = () => {
      const au = bgAudioRef.current;
      const vid = primaryVideoRef.current;
      if (document.hidden) {
        if (au) { au.muted = false; void au.play?.().catch(() => { /* */ }); }
        if (vid) vid.muted = true;
        void enterPip(vid);
      } else {
        if (au) au.muted = true;
        if (vid) vid.muted = false;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [rtc.peers.length, rtc.peers]);

  // pre91: Direkt-Anruf-Modus — nach Gesprächsende Fenster schließen (best effort; greift
  // nur bei skriptgeöffneten Fenstern / im Kiosk. Sonst bleibt der Verabschiedungsschirm).
  useEffect(() => {
    if (!autoClose || rtc.status !== 'ended') return;
    const t = setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 500);
    return () => clearTimeout(t);
  }, [autoClose, rtc.status]);

  const connecting = rtc.status === 'connecting' || (rtc.status === 'connected' && rtc.peers.length === 0);

  // Welle 8d/M2: Gespräch beendet → Verabschiedung statt „Verbindung wird hergestellt…".
  if (rtc.status === 'ended') {
    return <GuestEnded reason={rtc.endedReason} autoClose={autoClose} />;
  }

  return (
    <div className="gc-root">
      <style>{CALL_CSS}</style>

      {/* pre91: Ehrlicher Verschlüsselungs-Indikator. Gruppencall (Caller) kann E2EE (SFrame)
          erreichen; Experten-Strecken bleiben transportverschlüsselt (DTLS-SRTP). */}
      {rtc.peers.length > 0 && (
        <div style={{
          position: 'absolute', top: 12, left: 12, zIndex: 6,
          display: 'inline-flex', alignItems: 'center', gap: 5,
          background: rtc.e2eeActive ? 'rgba(16,185,129,0.20)' : 'rgba(245,158,11,0.20)',
          color: rtc.e2eeActive ? '#6ee7b7' : '#fde68a',
          padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          backdropFilter: 'blur(4px)',
        }}
        title={rtc.e2eeActive
          ? 'Ende-zu-Ende verschlüsselt (SFrame). Nur die Gesprächspartner können die Medien entschlüsseln.'
          : 'Transportverschlüsselt (DTLS-SRTP). Eine zusätzliche Ende-zu-Ende-Verschlüsselung ist hier nicht aktiv.'}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {rtc.e2eeActive ? 'Ende-zu-Ende verschlüsselt' : 'Transportverschlüsselt'}
        </div>
      )}

      <div className={`gc-stage gc-n${Math.min(rtc.peers.length, 2)}`}>
        {rtc.peers.length === 0 && (
          <div className="gc-tile gc-waiting">
            <div className="ga-spinner" />
            <div className="gc-waittext">
              {rtc.status === 'error' ? rtc.error : 'Verbindung wird hergestellt…'}
            </div>
          </div>
        )}
        {rtc.peers.map((p, i) => (
          <RemoteTile key={p.peerId} peer={p} videoRef={i === 0 ? primaryVideoRef : undefined} />
        ))}
      </div>

      {/* Eigenbild als Vorschau */}
      <div className={`gc-self ${rtc.cameraFacing === 'environment' ? 'rear' : ''}`}>
        <video ref={localRef} autoPlay playsInline muted className={rtc.camOn ? '' : 'gc-camoff'} />
        {!rtc.camOn && <div className="gc-camoff-label">Kamera aus</div>}
      </div>

      {/* pre217: separates Audio-Element für Hintergrund-Ton (iOS) — im Vordergrund stumm */}
      <audio ref={bgAudioRef} autoPlay playsInline style={{ display: 'none' }} />

      {/* Steuerleiste */}
      <div className="gc-bar">
        <button className={`gc-ctrl ${rtc.micOn ? '' : 'off'}`} onClick={rtc.toggleMic} title="Mikrofon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            {rtc.micOn ? (
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            ) : (
              <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
            )}
          </svg>
        </button>
        <button className={`gc-ctrl ${rtc.camOn ? '' : 'off'}`} onClick={rtc.toggleCam} title="Kamera">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            {rtc.camOn ? (
              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
            ) : (
              <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" />
            )}
          </svg>
        </button>
        {/* pre212: Button immer sichtbar — auf iPhone empirisch testbar; Ergebnis via screenShareError. */}
        <button className={`gc-ctrl ${rtc.screenSharing ? 'active' : ''}`} onClick={rtc.toggleScreen} title="Bildschirm teilen">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
          </svg>
        </button>
        {/* pre214: Manuelles Bild-im-Bild — zuverlässiger Fallback beim Wechsel in eine andere App. */}
        {rtc.peers.length > 0 && (
          <button className="gc-ctrl" onClick={() => void enterPip(primaryVideoRef.current)} title="Im Bild-im-Bild fortsetzen (z. B. beim Wechsel in eine andere App)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19 11h-8v6h8v-6zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zm-2 .02H3V4.97h18v14.05z" />
            </svg>
          </button>
        )}
        {/* pre215: Screenshot/Bild senden — öffnet auf Mobilgeräten Fotos/Kamera; geht P2P an den Berater. */}
        <button className="gc-ctrl" onClick={() => fileRef.current?.click()} title="Screenshot, Bild oder PDF an den Berater senden">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
          </svg>
        </button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = ''; // erlaubt erneute Auswahl derselben Datei
            if (!f) return;
            const ok = await rtc.sendFile(f);
            setImgHint(ok ? 'Datei an den Berater gesendet' : 'Senden nicht möglich (keine Verbindung)');
            setTimeout(() => setImgHint(null), 2500);
          }} />
        <button className="gc-ctrl gc-hangup" onClick={rtc.hangup} title="Verlassen">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
          </svg>
        </button>
      </div>

      {connecting && rtc.status !== 'error' && <div className="gc-hint">Bitte Kamera und Mikrofon erlauben</div>}
      {rtc.screenShareError && <div className="gc-hint">{rtc.screenShareError}</div>}
      {imgHint && <div className="gc-hint">{imgHint}</div>}
      {/* pre219: Touch-Signatur — der Berater hat ein PDF zur Unterschrift geschickt. */}
      {rtc.signRequest && (
        <ErrorBoundary onError={() => { setImgHint('Signatur-Ansicht fehlgeschlagen'); setTimeout(() => setImgHint(null), 2500); rtc.dismissSignature(); }} fallback={null}>
          <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: 'rgba(6,20,24,0.96)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cfe3e8' }}>Signatur wird geladen …</div>}>
          <GuestSignModal
            pdfBytes={rtc.signRequest.bytes}
            name={rtc.signRequest.name}
            signers={rtc.signRequest.signers}
            markers={rtc.signRequest.markers}
            requireIdent={rtc.signRequest.requireIdent}
            requireOtp={rtc.signRequest.requireOtp}
            onCancel={rtc.dismissSignature}
            onSubmit={async (signed, identProof, identStatus, otpCode) => {
              const ok = await rtc.submitSignature(signed, identProof, identStatus, otpCode);
              setImgHint(ok ? 'Unterschrift gesendet' : 'Senden fehlgeschlagen (keine Verbindung)');
              setTimeout(() => setImgHint(null), 2500);
            }}
          />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}

/** Welle 8d/M2: Verabschiedung nach Gesprächsende — mit erneutem Beitritt & Schließen. */
function GuestEnded({ reason, autoClose = false }: { reason: 'self' | 'remote' | null; autoClose?: boolean }) {
  const title = reason === 'remote' ? 'Das Gespräch wurde beendet' : 'Auf Wiedersehen';
  const msg =
    reason === 'remote'
      ? 'Der Berater hat das Gespräch beendet. Vielen Dank für Ihre Teilnahme.'
      : 'Sie haben das Gespräch verlassen. Vielen Dank für Ihre Teilnahme.';
  return (
    <div className="gc-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{CALL_CSS}</style>
      <div style={{ textAlign: 'center', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(95,212,196,0.16)', color: '#5fd4c4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 650, margin: 0, color: '#e8f1f1' }}>{title}</h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: '#aecccb', margin: 0 }}>{msg}</p>
        {autoClose && (
          <p style={{ fontSize: 13, lineHeight: 1.5, color: '#7fa6a5', margin: 0 }}>
            Das Fenster wird automatisch geschlossen. Sollte das nicht klappen, können Sie es jetzt schließen.
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 6 }}>
          {!autoClose && (
            <button
              onClick={() => window.location.reload()}
              style={{ width: '100%', padding: '14px 18px', fontSize: 16, fontWeight: 650, cursor: 'pointer', border: 'none', borderRadius: 12, background: '#2bb6a3', color: '#042' }}
            >Erneut beitreten</button>
          )}
          <button
            onClick={() => window.close()}
            style={{ width: '100%', padding: '13px 18px', fontSize: 15, fontWeight: 600, cursor: 'pointer', borderRadius: 12, background: 'transparent', color: '#aecccb', border: '1px solid rgba(255,255,255,0.18)' }}
          >Fenster schließen</button>
        </div>
      </div>
    </div>
  );
}

function RemoteTile({ peer, videoRef }: { peer: RemotePeer; videoRef?: React.MutableRefObject<HTMLVideoElement | null> }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && peer.stream) {
      ref.current.srcObject = peer.stream;
      ref.current.play().catch(() => { /* Autoplay ggf. blockiert */ });
    }
    if (videoRef) videoRef.current = ref.current; // pre214: Primär-Video für PiP durchreichen
  }, [peer.stream, videoRef]);
  const label =
    peer.role === 'stele' ? 'Beratungsstele' : peer.name || (peer.role === 'agent' ? 'Berater' : 'Teilnehmer');
  return (
    <div className="gc-tile">
      <video ref={ref} autoPlay playsInline className="gc-remote" />
      <div className="gc-label">{label}</div>
    </div>
  );
}

const CALL_CSS = `
.gc-root { position: fixed; inset: 0; background: #06171a; overflow: hidden; }
.gc-stage { position: absolute; inset: 0; display: grid; gap: 4px; }
.gc-stage.gc-n0, .gc-stage.gc-n1 { grid-template-rows: 1fr; }
.gc-stage.gc-n2 { grid-template-rows: 1fr 1fr; }
@media (min-aspect-ratio: 1/1) { .gc-stage.gc-n2 { grid-template-rows: 1fr; grid-template-columns: 1fr 1fr; } }
.gc-tile { position: relative; background: #0a2125; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.gc-remote { width: 100%; height: 100%; object-fit: cover; background: #0a2125; }
.gc-label {
  position: absolute; left: 12px; bottom: 12px;
  background: rgba(0,0,0,0.45); color: #e8f1f1; font-size: 13px; font-weight: 600;
  padding: 5px 11px; border-radius: 8px; backdrop-filter: blur(4px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.gc-waiting { flex-direction: column; gap: 14px; color: #aecccb; }
.gc-waittext { font-size: 15px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; text-align: center; padding: 0 24px; }
.ga-spinner { width: 38px; height: 38px; border-radius: 50%; border: 3px solid rgba(255,255,255,0.15); border-top-color: #5fd4c4; animation: ga-spin 0.9s linear infinite; }
@keyframes ga-spin { to { transform: rotate(360deg); } }
.gc-self {
  position: absolute; top: 16px; right: 16px; width: 104px; height: 150px;
  border-radius: 14px; overflow: hidden; border: 2px solid rgba(255,255,255,0.2);
  background: #0a2125; box-shadow: 0 8px 24px rgba(0,0,0,0.4); z-index: 5;
}
.gc-self video { width: 100%; height: 100%; object-fit: cover; transform: scaleX(-1); }
.gc-self.rear video { transform: none; } /* pre225: Rückkamera nicht spiegeln */
.gc-self video.gc-camoff { opacity: 0; }
.gc-camoff-label {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 11px; color: #7fa6a3; font-family: -apple-system, sans-serif;
}
.gc-bar {
  position: absolute; bottom: calc(20px + env(safe-area-inset-bottom, 0px)); left: 50%; transform: translateX(-50%);
  display: flex; gap: 12px; padding: 10px 14px; z-index: 10;
  background: rgba(10,33,37,0.85); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 18px; backdrop-filter: blur(10px);
}
.gc-ctrl {
  width: 52px; height: 52px; border-radius: 50%; border: none; cursor: pointer;
  font-size: 21px; background: rgba(255,255,255,0.12); color: #fff; transition: background 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.gc-ctrl:hover { background: rgba(255,255,255,0.2); }
.gc-ctrl.off { background: rgba(214,120,90,0.85); }
.gc-ctrl.active { background: #2bb6a3; }
.gc-hangup { background: #d65a5a; }
.gc-hangup:hover { background: #e26b6b; }
.gc-hint {
  position: absolute; bottom: calc(86px + env(safe-area-inset-bottom, 0px)); left: 50%; transform: translateX(-50%);
  font-size: 12.5px; color: #7fa6a3; font-family: -apple-system, sans-serif; white-space: nowrap;
}
`;
